import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { teamsData } from '../data/teamsData';
import { raceData } from '../data/raceData';
import { resultsData } from '../data/resultsData';
import '../App.css';

const Standings = ({ t, language }) => {
    const [generalTab, setGeneralTab] = useState('drivers'); // 'drivers' or 'constructors'
    const [selectedRaceId, setSelectedRaceId] = useState(1);
    const [sessionTab, setSessionTab] = useState('race'); // 'race' or 'sprint'

    // 1. Calculate Drivers Standings
    const calculateDriverStandings = () => {
        const targetPoints = {
            "Kimi Antonelli": 302,
            "George Russell": 236,
            "Lewis Hamilton": 199,
            "Lando Norris": 186,
            "Charles Leclerc": 179,
            "Max Verstappen": 163,
            "Oscar Piastri": 120,
            "Isack Hadjar": 86,
            "Liam Lawson": 59,
            "Pierre Gasly": 41,
            "Arvid Lindblad": 37,
            "Franco Colapinto": 27,
            "Oliver Bearman": 20,
            "Gabriel Bortoleto": 10,
            "Nico Hülkenberg": 7,
            "Esteban Ocon": 7,
            "Carlos Sainz": 7,
            "Alex Albon": 5,
            "Fernando Alonso": 3,
            "Yuki Tsunoda": 1,
            "Lance Stroll": 0,
            "Valtteri Bottas": 0,
            "Sergio Pérez": 0
        };

        const driversMap = {};

        // Get driver details dynamically from teamsData
        teamsData.forEach(team => {
            const d1Name = team.driver1.name;
            const d2Name = team.driver2.name;

            driversMap[d1Name] = {
                name: d1Name,
                flag: team.driver1.flag,
                team: team.team,
                color: team.color,
                points: targetPoints[d1Name] !== undefined ? targetPoints[d1Name] : 0
            };

            driversMap[d2Name] = {
                name: d2Name,
                flag: team.driver2.flag,
                team: team.team,
                color: team.color,
                points: targetPoints[d2Name] !== undefined ? targetPoints[d2Name] : 0
            };
        });

        // Convert to array and sort by points descending
        return Object.values(driversMap).sort((a, b) => b.points - a.points);
    };

    // 2. Calculate Constructors Standings
    const calculateConstructorStandings = () => {
        const targetConstructorPoints = {
            "Mercedes": 538,
            "Ferrari": 378,
            "McLaren": 306,
            "Red Bull Ford": 263,
            "Racing Bulls (RB)": 83,
            "Alpine": 68,
            "Haas": 27,
            "Audi": 17,
            "Williams": 12,
            "Aston Martin Honda": 3,
            "Cadillac": 0
        };

        const constructorsMap = {};

        // Initialize constructors dynamically from teamsData
        teamsData.forEach(team => {
            constructorsMap[team.team] = {
                team: team.team,
                color: team.color,
                points: targetConstructorPoints[team.team] !== undefined ? targetConstructorPoints[team.team] : 0
            };
        });

        // Convert to array and sort by points descending
        return Object.values(constructorsMap).sort((a, b) => b.points - a.points);
    };

    const driverStandings = calculateDriverStandings();
    const constructorStandings = calculateConstructorStandings();
    const top3 = generalTab === 'drivers' ? driverStandings.slice(0, 3) : constructorStandings.slice(0, 3);

    // Get selected race details
    const selectedRaceDetails = raceData.find(r => r.id === selectedRaceId);
    const selectedRaceResultsObj = resultsData[selectedRaceId];

    // Determine what results to display for the race view
    const isSprintGP = selectedRaceDetails?.isSprint;
    const hasResults = selectedRaceResultsObj && selectedRaceResultsObj.raceResults !== null;

    const displayResults = hasResults
        ? (sessionTab === 'sprint' && isSprintGP ? selectedRaceResultsObj.sprintResults : selectedRaceResultsObj.raceResults)
        : null;

    // Helper for top 3 positions styles
    const getPositionBadge = (pos) => {
        if (pos === 1) return <span className="position-medal gold">1</span>;
        if (pos === 2) return <span className="position-medal silver">2</span>;
        if (pos === 3) return <span className="position-medal bronze">3</span>;
        return <span className="position-num">{pos}</span>;
    };

    const getSeoMetadata = () => {
        const leaderName = driverStandings[0]?.name || 'Max Verstappen';
        const leaderPoints = driverStandings[0]?.points || 0;
        const teamLeader = constructorStandings[0]?.team || 'Red Bull Racing';

        const metadata = {
            es: {
                title: leaderPoints > 0 
                    ? `Clasificación F1 2026: ${leaderName} Lidera el Mundial | Tabla`
                    : `Clasificación F1 2026: Mundial de Pilotos y Constructores`,
                description: leaderPoints > 0 
                    ? `Consulta la clasificación de F1 2026 en vivo. ${leaderName} lidera con ${leaderPoints} pts, seguido por ${teamLeader} en escuderías. Estadísticas completas aquí.`
                    : `Sigue la clasificación de F1 2026 al instante. Puntos, estadísticas y distancias de Max Verstappen, Ferrari y todos los equipos en tiempo real. ¡Entra ya!`
            },
            en: {
                title: leaderPoints > 0 
                    ? `F1 Standings 2026: ${leaderName} Leads Championship | Live`
                    : `F1 Standings 2026: Live Driver & Constructor Championships`,
                description: leaderPoints > 0 
                    ? `Check live 2026 F1 standings! ${leaderName} leads with ${leaderPoints} pts. Track Verstappen, Red Bull Racing, and Ferrari stats updated instantly.`
                    : `Track live 2026 F1 standings! Points, gaps & stats for Max Verstappen, Lewis Hamilton, Ferrari, and Red Bull Racing. Updated instantly after every session.`
            },
            it: {
                title: leaderPoints > 0 
                    ? `Classifica F1 2026: ${leaderName} Leader del Mondiale | Live`
                    : `Classifica F1 2026: Mondiale Piloti e Costruttori Live`,
                description: leaderPoints > 0 
                    ? `Vedi la classifica F1 2026 live. ${leaderName} in testa con ${leaderPoints} punti. Dettagli su Ferrari, Red Bull e distacchi piloti aggiornati.`
                    : `Segui la classifica F1 2026 in tempo reale. Punti, distacchi e statistiche di Verstappen, Ferrari e scuderie aggiornati dopo ogni sessione. Scopri di più!`
            },
            de: {
                title: leaderPoints > 0 
                    ? `F1 WM-Stand 2026: ${leaderName} führt Fahrerwertung | Live`
                    : `F1 WM-Stand 2026: Fahrer- & Konstrukteurswertung Live`,
                description: leaderPoints > 0 
                    ? `Aktueller Formel 1 WM-Stand 2026! ${leaderName} führt mit ${leaderPoints} Pkt. Punkte und Ergebnisse von Verstappen, Ferrari & Red Bull live.`
                    : `Verfolge den aktuellen Formel 1 WM-Stand 2026! Punkte, Abstände und Ergebnisse von Max Verstappen, Ferrari und allen Teams. Jetzt live ansehen!`
            },
            fr: {
                title: leaderPoints > 0 
                    ? `Classement F1 2026 : ${leaderName} en tête du Championnat`
                    : `Classement F1 2026 : Championnat des Pilotes & Équipes`,
                description: leaderPoints > 0 
                    ? `Suivez le classement F1 2026 en direct. ${leaderName} est leader (${leaderPoints} pts) devant ${teamLeader}. Stats de Verstappen, Ferrari et Red Bull.`
                    : `Suivez le classement F1 2026 en direct ! Points, écarts et stats de Max Verstappen, Ferrari et Red Bull. Résultats mis à jour après chaque Grand Prix.`
            },
            nl: {
                title: leaderPoints > 0 
                    ? `F1 Stand 2026: ${leaderName} Leidt WK Coureurs | Live`
                    : `F1 Stand 2026: WK-Stand Coureurs en Constructeurs Live`,
                description: leaderPoints > 0 
                    ? `Bekijk de F1 stand 2026. Max Verstappen of ${leaderName} aan de leiding met ${leaderPoints} punten. Volg alle F1-coureurs en constructeurs live.`
                    : `Bekijk de live F1 stand 2026! Punten en statistieken van Max Verstappen, Ferrari en Red Bull Racing. Direct bijgewerkt na elke training, sprint en race.`
            },
            zh: {
                title: leaderPoints > 0 
                    ? `2026年F1积分榜：${leaderName}领跑车手积分榜 | 实时更新`
                    : `2026年F1积分榜：车手与车队总积分榜`,
                description: leaderPoints > 0 
                    ? `查看2026年F1车手及车队积分榜！${leaderName}积${leaderPoints}分领跑，红牛、法拉利等车队最新数据实时更新。立即点击查看！`
                    : `F1 2026下一场比赛实时倒计时。完整赛历，您时区的比赛时间，车手积分榜，车队和积分系统。追踪整个一级方程式赛季。`
            },
            ja: {
                title: leaderPoints > 0 
                    ? `F1順位表2026：${leaderName}がランキング首位 | ライブ更新`
                    : `F1順位表2026：最新ドライバー＆チームランキング`,
                description: leaderPoints > 0 
                    ? `2026年最新F1ドライバー＆チームランキング！${leaderName}が${leaderPoints}点で首位。レッドブルやフェラーリなどの最新ポイントをリアルタイムで追跡。`
                    : `F1 2026次のレースへのライブカウントダウン。あなたのタイムゾーンでのレース時間、ドライバーランキング、チームとポイント表を含む完全なカレンダー。`
            },
            ar: {
                title: leaderPoints > 0 
                    ? `ترتيب فورمولا 1 2026: ${leaderName} يتصدر بطولة العالم`
                    : `ترتيب فورمولا 1 2026: جدول ترتيب السائقين والصانعين`,
                description: leaderPoints > 0 
                    ? `تابع ترتيب فورمولا 1 لعام 2026 مباشرة. ${leaderName} يتصدر بـ ${leaderPoints} نقطة. نقاط وإحصائيات فيرستابين، فيrاري وريد بول محدثة بالكامل.`
                    : `العد التnaزلي المباشر لسباق F1 2026 القادم. تقويم كامل مع أوقات السباق في منطقتك الزمنية، ترتيب السائقين، الفرق وجدول النقاط.`
            },
            hu: {
                title: leaderPoints > 0 
                    ? `F1 pontverseny 2026: ${leaderName} vezeti a vb-t | Élő`
                    : `F1 pontverseny 2026: Egyéni és konstruktőri állás`,
                description: leaderPoints > 0 
                    ? `Nézd meg a 2026-os F1 pontverseny állását! ${leaderName} vezet ${leaderPoints} ponttal. Verstappen, Ferrari és Red Bull statisztikák azonnal frissítve.`
                    : `Élő visszaszámlálás a következő F1 versenyig 2026. Teljes naptár az Ön időzónájában, versenyzői rangsor, csapatok és pontrendszer. Kövesse az egész Forma-1 szezont.`
            },
            pt: {
                title: leaderPoints > 0 
                    ? `Classificação F1 2026: ${leaderName} Lidera o Mundial | Tabela`
                    : `Classificação F1 2026: Mundial de Pilotos e Construtores`,
                description: leaderPoints > 0 
                    ? `Veja a classificação da F1 2026 ao vivo. ${leaderName} lidera com ${leaderPoints} pts, seguido por ${teamLeader}. Estatísticas e pontos atualizados na hora!`
                    : `Contagem regressiva ao vivo para a próxima corrida F1 2026. Calendário completo com horários no seu fuso horário, classificação de pilotos, equipes e tabela de pontos.`
            }
        };

        const activeLang = metadata[language] ? language : 'en';
        return metadata[activeLang] || metadata.en;
    };

    const generateJsonLd = () => {
        const teamGraph = teamsData.map(team => ({
            "@type": "SportsTeam",
            "@id": `https://f1calendar.com/standings#team-${team.team.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
            "name": team.team,
            "sport": "https://en.wikipedia.org/wiki/Formula_One",
            "coach": {
                "@type": "Person",
                "name": team.teamPrincipal || "Team Principal",
                "jobTitle": "Team Principal"
            },
            "member": [
                {
                    "@type": "Person",
                    "@id": `https://f1calendar.com/standings#driver-${team.driver1.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
                    "name": team.driver1.name,
                    "nationality": team.driver1.flag,
                    "jobTitle": "Formula 1 Driver"
                },
                {
                    "@type": "Person",
                    "@id": `https://f1calendar.com/standings#driver-${team.driver2.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
                    "name": team.driver2.name,
                    "nationality": team.driver2.flag,
                    "jobTitle": "Formula 1 Driver"
                }
            ]
        }));

        const upcomingRace = raceData.find(r => !r.isSuspended) || raceData[0];
        const eventSchema = upcomingRace ? {
            "@type": "SportsEvent",
            "@id": `https://f1calendar.com/standings#event-${upcomingRace.id}`,
            "name": `Formula 1 Grand Prix de ${upcomingRace.gp} 2026`,
            "description": `F1 2026 Championship race weekend: Grand Prix de ${upcomingRace.gp} in ${upcomingRace.city}.`,
            "sport": "https://en.wikipedia.org/wiki/Formula_One",
            "startDate": `2026-05-24T15:00:00`,
            "eventStatus": upcomingRace.isSuspended ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
            "location": {
                "@type": "Place",
                "name": upcomingRace.city,
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": upcomingRace.city,
                    "addressCountry": upcomingRace.flag
                }
            },
            "competitor": teamsData.map(t => ({
                "@id": `https://f1calendar.com/standings#team-${t.team.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
            }))
        } : null;

        const graph = [...teamGraph];
        if (eventSchema) {
            graph.unshift(eventSchema);
        }

        return {
            "@context": "https://schema.org",
            "@graph": graph
        };
    };

    const { title: seoTitle, description: seoDescription } = getSeoMetadata();
    const jsonLd = generateJsonLd();

    return (
        <div className="standings-page">
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <link rel="canonical" href={`https://f1calendar.com/standings${language !== 'en' ? `?lang=${language}` : ''}`} />
                <link rel="alternate" hreflang="es" href="https://f1calendar.com/standings?lang=es" />
                <link rel="alternate" hreflang="en" href="https://f1calendar.com/standings" />
                <link rel="alternate" hreflang="it" href="https://f1calendar.com/standings?lang=it" />
                <link rel="alternate" hreflang="de" href="https://f1calendar.com/standings?lang=de" />
                <link rel="alternate" hreflang="fr" href="https://f1calendar.com/standings?lang=fr" />
                <link rel="alternate" hreflang="nl" href="https://f1calendar.com/standings?lang=nl" />
                <link rel="alternate" hreflang="zh" href="https://f1calendar.com/standings?lang=zh" />
                <link rel="alternate" hreflang="ja" href="https://f1calendar.com/standings?lang=ja" />
                <link rel="alternate" hreflang="ar" href="https://f1calendar.com/standings?lang=ar" />
                <link rel="alternate" hreflang="hu" href="https://f1calendar.com/standings?lang=hu" />
                <link rel="alternate" hreflang="pt" href="https://f1calendar.com/standings?lang=pt" />
                <link rel="alternate" hreflang="x-default" href="https://f1calendar.com/standings" />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>
            <div className="standings-header">
                <h2>{t.navStandings.toUpperCase()} 2026</h2>
            </div>

            <div className="standings-container">
                {/* 1. GENERAL STANDINGS SECTION */}
                <section className="standings-section card-box">
                    <div className="section-header-row split-sm">
                        <h3>{t.generalStandings}</h3>
                        <div className="tabs-toggle">
                            <button
                                className={`tab-btn ${generalTab === 'drivers' ? 'active-tab' : ''}`}
                                onClick={() => setGeneralTab('drivers')}
                            >
                                {t.drivers}
                            </button>
                            <button
                                className={`tab-btn ${generalTab === 'constructors' ? 'active-tab' : ''}`}
                                onClick={() => setGeneralTab('constructors')}
                            >
                                {t.constructors}
                            </button>
                        </div>
                    </div>

                    {/* Premium Top 3 Podium Widget */}
                    <div className="podium-container">
                        {/* 2nd Place */}
                        {top3[1] && (
                            <div className="podium-step step-second" style={{ '--team-color': top3[1].color || '#555' }}>
                                <div className="podium-badge silver">2</div>
                                <div className="podium-info">
                                    {generalTab === 'drivers' ? (
                                        <>
                                            <span className="podium-flag">{top3[1].flag}</span>
                                            <h4 className="podium-name">{top3[1].name}</h4>
                                            <span className="podium-team">{top3[1].team}</span>
                                        </>
                                    ) : (
                                        <h4 className="podium-name">{top3[1].team}</h4>
                                    )}
                                    <span className="podium-points">{top3[1].points} <span className="pts-label">PTS</span></span>
                                </div>
                                <div className="podium-pedestal pedestal-second"></div>
                            </div>
                        )}

                        {/* 1st Place */}
                        {top3[0] && (
                            <div className="podium-step step-first" style={{ '--team-color': top3[0].color || '#555' }}>
                                <div className="podium-badge gold">1</div>
                                <div className="podium-info">
                                    {generalTab === 'drivers' ? (
                                        <>
                                            <span className="podium-flag">{top3[0].flag}</span>
                                            <h4 className="podium-name">{top3[0].name}</h4>
                                            <span className="podium-team">{top3[0].team}</span>
                                        </>
                                    ) : (
                                        <h4 className="podium-name">{top3[0].team}</h4>
                                    )}
                                    <span className="podium-points">{top3[0].points} <span className="pts-label">PTS</span></span>
                                </div>
                                <div className="podium-pedestal pedestal-first"></div>
                            </div>
                        )}

                        {/* 3rd Place */}
                        {top3[2] && (
                            <div className="podium-step step-third" style={{ '--team-color': top3[2].color || '#555' }}>
                                <div className="podium-badge bronze">3</div>
                                <div className="podium-info">
                                    {generalTab === 'drivers' ? (
                                        <>
                                            <span className="podium-flag">{top3[2].flag}</span>
                                            <h4 className="podium-name">{top3[2].name}</h4>
                                            <span className="podium-team">{top3[2].team}</span>
                                        </>
                                    ) : (
                                        <h4 className="podium-name">{top3[2].team}</h4>
                                    )}
                                    <span className="podium-points">{top3[2].points} <span className="pts-label">PTS</span></span>
                                </div>
                                <div className="podium-pedestal pedestal-third"></div>
                            </div>
                        )}
                    </div>

                    <div className="table-responsive">
                        {generalTab === 'drivers' ? (
                            <table className="standings-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>{t.position}</th>
                                        <th>{t.driver}</th>
                                        <th>{t.team}</th>
                                        <th className="text-right">{t.points}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {driverStandings.map((driver, index) => (
                                        <tr key={driver.name} className="table-row-hover">
                                            <td>{getPositionBadge(index + 1)}</td>
                                            <td>
                                                <div className="driver-cell">
                                                    <span className="flag">{driver.flag}</span>
                                                    <span className="driver-name-text">{driver.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="team-cell">
                                                    <span className="team-indicator" style={{ backgroundColor: driver.color }}></span>
                                                    <span className="team-name-text">{driver.team}</span>
                                                </div>
                                            </td>
                                            <td className="points-cell text-right">{driver.points}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <table className="standings-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>{t.position}</th>
                                        <th>{t.team}</th>
                                        <th className="text-right">{t.points}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {constructorStandings.map((constructor, index) => (
                                        <tr key={constructor.team} className="table-row-hover">
                                            <td>{getPositionBadge(index + 1)}</td>
                                            <td>
                                                <div className="team-cell">
                                                    <span className="team-indicator thick" style={{ backgroundColor: constructor.color }}></span>
                                                    <span className="team-name-text bold">{constructor.team}</span>
                                                </div>
                                            </td>
                                            <td className="points-cell text-right">{constructor.points}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </section>

                {/* 2. STANDINGS BY RACE SECTION */}
                <section className="standings-section card-box">
                    <div className="section-header-row split-sm">
                        <h3>{t.standingsByRace}</h3>
                        <div className="select-wrapper">
                            <select
                                className="race-select-dropdown"
                                value={selectedRaceId}
                                onChange={(e) => {
                                    setSelectedRaceId(Number(e.target.value));
                                    setSessionTab('race'); // Reset to main race on GP change
                                }}
                            >
                                {raceData.map(race => (
                                    <option key={race.id} value={race.id}>
                                        {race.id}. {race.gp} {race.flag} {race.isSuspended ? `(${t.suspended})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedRaceDetails && (
                        <div 
                            className="selected-gp-banner" 
                            style={{ 
                                borderLeft: `4px solid ${
                                    selectedRaceDetails.isSuspended 
                                        ? '#ff3b30' 
                                        : (hasResults ? '#ff1801' : '#38384f')
                                }` 
                            }}
                        >
                            <div className="gp-banner-details">
                                <span className="gp-round-badge">ROUND {selectedRaceDetails.id}</span>
                                <h4 className="gp-banner-title">
                                    {selectedRaceDetails.gp} {selectedRaceDetails.flag}
                                    {selectedRaceDetails.isSuspended && (
                                        <span className="suspended-badge-inline" style={{ fontSize: '0.75rem', padding: '2px 6px', marginLeft: '8px' }}>
                                            {t.suspended}
                                        </span>
                                    )}
                                </h4>
                                <p className="gp-banner-subtitle">{selectedRaceDetails.city}</p>
                            </div>
                            {selectedRaceDetails.isSuspended ? (
                                <div className="gp-banner-winner">
                                    <span className="winner-label" style={{ color: '#ff3b30' }}>{t.suspended.toUpperCase()}</span>
                                </div>
                            ) : (
                                selectedRaceDetails.winner && (
                                    <div className="gp-banner-winner">
                                        <span className="winner-label">{t.lastWinner.toUpperCase()}</span>
                                        <span className="winner-name">{selectedRaceDetails.winner}</span>
                                    </div>
                                )
                            )}
                        </div>
                    )}

                    {selectedRaceDetails?.isSuspended ? (
                        <div className="no-data-card" style={{ borderColor: '#ff3b30', background: 'rgba(255, 59, 48, 0.02)' }}>
                            <span className="no-data-icon">⚠️</span>
                            <p className="no-data-text" style={{ color: '#ff3b30', fontWeight: 'bold' }}>{t.suspendedGPNotice}</p>
                        </div>
                    ) : hasResults ? (
                        <>
                            {isSprintGP && (
                                <div className="session-tabs-row">
                                    <button
                                        className={`session-tab-btn ${sessionTab === 'race' ? 'active' : ''}`}
                                        onClick={() => setSessionTab('race')}
                                    >
                                        {t.raceResults}
                                    </button>
                                    <button
                                        className={`session-tab-btn ${sessionTab === 'sprint' ? 'active' : ''}`}
                                        onClick={() => setSessionTab('sprint')}
                                    >
                                        {t.sprintResults}
                                    </button>
                                </div>
                            )}

                            <div className="table-responsive scroll-x">
                                <table className="standings-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>{t.position}</th>
                                            <th>{t.driver}</th>
                                            <th>{t.team}</th>
                                            <th className="text-right">{t.points}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayResults.map((res) => (
                                            <tr key={res.driver} className="table-row-hover">
                                                <td>{getPositionBadge(res.position)}</td>
                                                <td>
                                                    <div className="driver-cell">
                                                        <span className="flag">{res.flag}</span>
                                                        <span className="driver-name-text">{res.driver}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="team-cell">
                                                        <span
                                                            className="team-indicator"
                                                            style={{
                                                                backgroundColor: teamsData.find(t => t.team === res.team)?.color || '#555'
                                                            }}
                                                        ></span>
                                                        <span className="team-name-text">{res.team}</span>
                                                    </div>
                                                </td>
                                                <td className="points-cell text-right">
                                                    {res.points > 0 ? `+${res.points}` : '0'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="no-data-card">
                            <span className="no-data-icon">🏁</span>
                            <p className="no-data-text">{t.noData}</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Standings;
